<template>
  <RightSheet style="width: 80vw;" v-model="dialogVisible" max-width="1920">
    <div class="drawer-wrapper">
      <!-- 标题 -->
      <div class="drawer-title">
        <v-btn icon variant="text" class="back-btn" @click="closeDrawer">
          <v-icon>mdi-reply-outline</v-icon>
          返回
        </v-btn>
        <span class="device-name">{{ deviceData?.photovoltaicName || '光伏逆变器详情' }}</span>
        <v-chip size="small" class="comm-status" :color="isOnline ? 'green' : 'red'">
          {{ isOnline ? '在线' : '离线' }}
        </v-chip>
        <span class="sample-time" v-if="sampleTime">数据采样时间：{{ sampleTime }}</span>
      </div>

      <DataCardPropsProvide title-position="bottom" :value-style="{ fontSize: '16px', fontWeight: 700 }">
        <div class="drawer-main">
          <div class="detail-content">
            <!-- 实时数据 -->
            <div class="section">
              <div class="section-header">
                <v-icon class="section-icon" color="#3A68FF" size="20">mdi-information-outline</v-icon>
                <span>实时数据</span>
              </div>
              <div class="section-grid">
                <DataCard v-for="field in modelArray" :key="field.key" class="field-card" :title="field.nameCn"
                  :value="getFieldDisplay(field).value" :unit="getFieldDisplay(field).unit" />
              </div>
            </div>
          </div>
        </div>
      </DataCardPropsProvide>

      <!-- 关闭按钮 -->
      <v-btn icon size="small" variant="text" class="close-btn" @click="closeDrawer">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </RightSheet>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import RightSheet from '@/components/RightSheet/index.vue'
import DataCard from '@/components/DataCard/index.vue'
import DataCardPropsProvide from '@/components/DataCard/PropProvide.vue'
import dayjs from 'dayjs'
import * as api from '@/api/device/pv'
import { useRealData } from '@/hooks/useRealData'
import { fmtw, fmtku } from '@/utils/fortmatter.js'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  stationId: {
    type: [Number, String],
  },
  deviceData: {
    type: Object,
    default: () => ({})
  },
  modelArray: {
    type: Array,
    default: () => []
  },
  modelData: {
    type: Object,
    default: () => ({})
  },
  isOnline: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const closeDrawer = () => {
  dialogVisible.value = false
}

const sampleTime = computed(() => {
  const time = realtime.value?.record_time || realtime.value?.sampTime
    || props.deviceData?.record_time || props.deviceData?.sampTime
  if (!time) return ''
  return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
})

// // 通信状态：实时数据中 p / i_a / u_ab 任一为数值即视为在线
// const isOnline = computed(() => {
//   const data = realtime.value && Object.keys(realtime.value).length
//     ? realtime.value
//     : (props.deviceData?.data || props.deviceData || {})
//   return typeof data.p === 'number' || typeof data.i_a === 'number' || typeof data.u_ab === 'number'
// })


// 从数据中尝试多个可能的 key 获取值
function pickValue(data, keys) {
  for (const key of keys) {
    const val = data[key]
    if (val !== undefined && val !== null && val !== '') {
      return val
    }
  }
  return null
}

// 获取字段展示值
function getFieldDisplay(field) {
  const value = realtime.value[field.nameEn]
  const display = getRealValue(field.nameEn, value)
  if (display === '--' || Number.isNaN(Number(display))) {
    return { value: display, unit: field.unit }
  }
  if (String(field.unit).toLowerCase() === 'w') {
    return fmtw(display)
  }
  // kW / kWh / kVar / kΩ 等超过 1000 时升位到 M、G
  return fmtku(display, field.unit)
}


function getRealValue(key, value) {
  const model = props.modelData[key]
  if (model && model.readConverter) {
    const map = {}
    model.readConverter.split(',').map(item => {
      const [value, label] = item.split(':')
      map[value.trim()] = label.trim()
    })
    return map[value] ?? '--';
  }

  return value ?? '--'
}

// ─── 数据获取 ───
const realtime = ref({})

const { start, stop, updateOnce } = useRealData({
  async updateFunction() {
    const r = await api.getPvDataDetailRealtime(props.stationId, props.deviceData.uuid)
    return r.data
  },
  immediate: false,
  onUpdate(d) {
    console.log('~~~~~~', d)
    realtime.value = d
  }
})

watch(dialogVisible, (newVal) => {
  if (newVal) {
    start()
  } else {
    stop()
  }
})

watch(() => props.stationId, (newVal) => {
  updateOnce()
})


</script>

<style scoped lang="scss">
.drawer-wrapper {
  position: relative;
  width: 100%;
  height: 100vh;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
  display: flex;
  flex-direction: column;
}

.drawer-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  margin-bottom: 20px;
  padding-left: 24px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.device-name {
  margin-left: 4px;
}

.comm-status {
  margin-left: 12px;
}

.back-btn {
  width: 98px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #C0CFDA;
  margin-right: 10px;
  margin-left: -10px;
}

.sample-time {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  position: absolute;
  right: 84px;
  top: 26px;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
}

.drawer-main {
  flex: 1;
  overflow-y: auto;
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px 28px;
  margin-bottom: 20px;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: rgba(46, 46, 46, 1);
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(236, 236, 236, 1);
}

.section-icon {
  opacity: 0.8;
}

.section-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.field-card {
  background: rgba(239, 244, 251, 0.7);
  border-radius: 10px;
  padding: 16px;
}
</style>
